/**
 * n8n Webhook API Integration
 *
 * Handles calls to n8n workflow webhooks for:
 * - Draft generation from RSS content
 * - Post approval and publishing
 * - New post creation from URL or manual entry
 */

const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;
const N8N_DRAFT_WEBHOOK_URL = process.env.REACT_APP_N8N_DRAFT_WEBHOOK_URL;
const N8N_CREATE_POST_WEBHOOK_URL = process.env.REACT_APP_N8N_CREATE_POST_WEBHOOK_URL;

/**
 * Create a fetch request with timeout and better error handling
 * @param {string} url - Webhook URL
 * @param {Object} payload - Request payload
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
const makeWebhookRequest = async (url, payload, timeout = 30000) => {
  const startTime = Date.now();
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    console.log(`⏱️ [Webhook] Making request to: ${url}`, {
      action: payload.action,
      type: payload.type,
      timeout: `${timeout / 1000}s`,
    });

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        ...payload,
        timestamp: Date.now(),
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const duration = Date.now() - startTime;

    if (!response.ok) {
      let errorText = await response.text();
      console.error(`❌ [Webhook] Request failed (${duration}ms): ${response.status}`, errorText);

      // Try to parse error message for better user feedback
      let userFriendlyError = `Webhook failed: ${response.status} ${response.statusText}`;

      // Check for common error patterns
      if (errorText.includes("duplicate key") || errorText.includes("unique constraint")) {
        if (errorText.includes("sourceURL")) {
          userFriendlyError = "This URL has already been processed. Please try a different URL.";
        } else {
          userFriendlyError =
            "A duplicate entry was detected. Please try again with different content.";
        }
      }

      throw new Error(userFriendlyError);
    }

    // Handle empty response
    const responseText = await response.text();
    if (!responseText || responseText.trim() === "") {
      console.error(`❌ [Webhook] Empty response received (${duration}ms)`);
      throw new Error(
        "Workflow error: Empty response received. Please check the n8n workflow logs."
      );
    }

    // Try to parse JSON
    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error(`❌ [Webhook] Invalid JSON response (${duration}ms):`, responseText);

      // Check if response contains error patterns
      if (responseText.includes("duplicate key") || responseText.includes("unique constraint")) {
        if (responseText.includes("sourceURL")) {
          throw new Error("This URL has already been processed. Please try a different URL.");
        } else {
          throw new Error(
            "A duplicate entry was detected. Please try again with different content."
          );
        }
      }

      throw new Error(`Invalid response from workflow: ${parseError.message}`);
    }

    console.log(`✅ [Webhook] Response received (${duration}ms):`, result);

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`❌ [Webhook] Request error (${duration}ms):`, error.message);

    if (error.name === "AbortError") {
      console.warn(
        `⏰ [Webhook] Request timed out after ${timeout / 1000}s - workflow may still be processing`
      );
      return {
        success: false,
        error: `Request timed out after ${timeout / 1000} seconds`,
        isTimeout: true,
      };
    }

    if (error.message.includes("CORS") || error.message.includes("Access-Control")) {
      return {
        success: false,
        error: "CORS error - please check n8n webhook CORS configuration",
      };
    }

    if (error.message.includes("Failed to fetch")) {
      return {
        success: false,
        error: "Network error - please check if n8n server is accessible",
      };
    }

    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Trigger n8n webhook to process RSS content for draft generation
 *
 * Simplified workflow:
 * 1. RSS Content status already changed to "Processing"
 * 2. Sends webhook trigger with record ID to n8n
 * 3. n8n queries Supabase for the specific RSS Content record
 * 4. n8n processes via AI and creates Blog Post with "Pending Approval" status
 * 5. n8n creates relational link: Blog Posts["rss_content_id"] = rssContentId
 * 6. Frontend polls until Blog Post appears
 *
 * @param {string} rssContentId - Supabase record ID for RSS Content
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const triggerDraftGeneration = async (rssContentId) => {
  const webhookUrl = N8N_DRAFT_WEBHOOK_URL || N8N_WEBHOOK_URL;

  if (!webhookUrl) {
    throw new Error("N8N Webhook URL not configured. Please check environment variables.");
  }

  // Validate record ID exists
  if (!rssContentId) {
    throw new Error("RSS Content record ID is required");
  }

  console.log(`Triggering draft generation for RSS Content ID: ${rssContentId}`);

  return makeWebhookRequest(
    webhookUrl,
    {
      action: "generate_draft",
      rssContentId: rssContentId, // Primary field for n8n workflow
      postId: rssContentId, // Keep for backward compatibility if needed
    },
    120000
  ); // 120 second (2 minute) timeout for draft generation - AI processing can take time
};

// Keep the old function name for backward compatibility
export const generateDraft = triggerDraftGeneration;

/**
 * Trigger n8n webhook to approve and publish post
 *
 * Simplified workflow:
 * 1. Blog Post status already changed to "Approving"
 * 2. Sends webhook trigger with post ID and action
 * 3. n8n queries Supabase for the "Approving" record
 * 4. n8n processes publishing and updates status to "Posted"
 * 5. Frontend polls until post appears in Posted tab
 *
 * @param {string} postId - Blog Post record ID
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const triggerApproval = async (postId) => {
  if (!N8N_WEBHOOK_URL) {
    throw new Error("N8N Webhook URL not configured. Please check environment variables.");
  }

  return makeWebhookRequest(
    N8N_WEBHOOK_URL,
    {
      action: "approve",
      postId: postId,
    },
    120000
  ); // 120 second (2 minute) timeout for approval (publishing to multiple platforms takes time)
};

/**
 * Trigger n8n webhook to reject post
 *
 * Simplified workflow:
 * 1. Blog Post status already changed to "Rejecting"
 * 2. Sends webhook trigger with post ID and action
 * 3. n8n queries Supabase for the "Rejecting" record
 * 4. n8n processes rejection and updates status to "Rejected"
 * 5. Frontend polls until post appears in Rejected tab
 *
 * @param {string} postId - Blog Post record ID
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const triggerRejection = async (postId) => {
  if (!N8N_WEBHOOK_URL) {
    throw new Error("N8N Webhook URL not configured. Please check environment variables.");
  }

  return makeWebhookRequest(
    N8N_WEBHOOK_URL,
    {
      action: "reject",
      postId: postId,
    },
    30000
  ); // 30 second timeout for rejection
};

/**
 * Request edit for post (legacy function - kept for backward compatibility)
 * @param {string} postId - Post ID
 * @param {string} editNotes - Edit notes/instructions
 * @returns {Promise<import('../types').ApiResponse>}
 */
export const requestEdit = async (postId, editNotes) => {
  if (!N8N_WEBHOOK_URL) {
    throw new Error("N8N Webhook URL not configured. Please check environment variables.");
  }

  return makeWebhookRequest(
    N8N_WEBHOOK_URL,
    {
      action: "request_edit",
      postId,
      editNotes,
    },
    30000
  ); // 30 second timeout for edit requests
};

/**
 * Trigger n8n webhook for AI-powered content rewrite
 *
 * Workflow:
 * 1. Blog Post status already changed to "Editing"
 * 2. Sends webhook trigger with post ID and edit instructions
 * 3. n8n queries Supabase for the "Editing" record
 * 4. n8n calls AI to regenerate content based on instructions
 * 5. n8n updates Blog Post with new content
 * 6. n8n sets status to "Pending Approval"
 * 7. Frontend polls until status changes back
 *
 * @param {string} postId - Blog Post record ID
 * @param {Object} editData - Edit instructions and custom prompt
 * @param {string} editData.editInstructions - Main edit instructions
 * @param {string} editData.customPrompt - Additional custom prompt
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const triggerEditWithAI = async (postId, editData) => {
  if (!N8N_WEBHOOK_URL) {
    throw new Error("N8N Webhook URL not configured. Please check environment variables.");
  }

  return makeWebhookRequest(
    N8N_WEBHOOK_URL,
    {
      action: "edit_ai",
      postId: postId,
      editInstructions: editData.editInstructions || "",
      customPrompt: editData.customPrompt || "",
    },
    120000
  ); // 120 second (2 minute) timeout for AI rewrite - AI processing takes time
};

/**
 * Trigger manual edit update (direct database update, no n8n webhook)
 * Note: This function is a placeholder for consistency. Manual edits
 * are handled directly via the Supabase API in the useEdit hook.
 *
 * @param {string} postId - Blog Post record ID
 * @param {Object} editedContent - Edited content fields
 * @param {string} editedContent.article - Main article content
 * @param {string} editedContent.linkedin_post - LinkedIn post content
 * @param {string} editedContent.x_post - X/Twitter post content
 * @param {string} editedContent.threads_post - Threads post content
 * @returns {Promise<{success: boolean}>}
 */
export const triggerManualEdit = async (postId, editedContent) => {
  // Manual edits don't use webhooks - they update Supabase directly
  // This function exists for API consistency
  return {
    success: true,
    message: "Manual edit will be processed directly via Supabase API",
  };
};

/**
 * Trigger n8n webhook to create a new post from URL or manual entry
 *
 * Workflow:
 * 1. User submits either:
 *    a) URL (source type auto-detected by n8n)
 *    b) Manual entry with topic and content
 * 2. Sends webhook trigger to n8n with creation data
 * 3. n8n auto-detects URL source and processes content (scrapes URL or uses manual input)
 * 4. n8n calls AI to generate blog post content
 * 5. n8n creates a new Blog Post record with "Pending Approval" status
 * 6. Frontend polls or refreshes to show the new draft
 *
 * Note: The webhook may timeout but the n8n workflow continues in the background.
 * The frontend uses polling to detect when the Blog Post is created.
 *
 * @param {Object} creationData - Post creation data
 * @param {string} creationData.type - Creation type: "url" or "manual"
 * @param {string} [creationData.url] - URL to scrape (if type is "url", source auto-detected)
 * @param {string} [creationData.topic] - Topic/title (if type is "manual")
 * @param {string} [creationData.content] - Manual content (if type is "manual")
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const triggerNewPostCreation = async (creationData) => {
  const webhookUrl = N8N_CREATE_POST_WEBHOOK_URL || N8N_WEBHOOK_URL;

  if (!webhookUrl) {
    throw new Error("N8N Webhook URL not configured. Please check environment variables.");
  }

  console.log("🚀 [Webhook] Triggering new post creation:", creationData);

  // Validate creation data
  if (!creationData || !creationData.type) {
    throw new Error("Invalid creation data: type is required");
  }

  if (creationData.type === "url") {
    if (!creationData.url) {
      throw new Error("URL is required for URL-based creation");
    }
  } else if (creationData.type === "manual") {
    if (!creationData.topic || !creationData.content) {
      throw new Error("Topic and content are required for manual creation");
    }
  } else {
    throw new Error(`Invalid creation type: ${creationData.type}`);
  }

  // Use shorter timeout since the workflow response is quick
  // The actual processing happens in the background
  const result = await makeWebhookRequest(
    webhookUrl,
    {
      action: "create_post",
      ...creationData,
    },
    30000 // 30 second timeout - n8n responds quickly, processing happens async
  );

  console.log("✅ [Webhook] Post creation webhook result:", result);
  return result;
};
